import { atom, map } from 'nanostores';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  GetCurrentUserOutput,
  signOut,
} from 'aws-amplify/auth';
import { CustomerInfo, LogLevel, Purchases } from '@revenuecat/purchases-js';
import { $isOnline } from './app';
// import { AuthSession } from '@aws-amplify/core/src/singleton/Auth/types';

export type UserData = {
  customerInfo?: CustomerInfo;
  data?: {
    username: string;
    licenseGroup?: string;
    userId: string;
    signInDetails: any;
  };
  isAdmin?: boolean;
  isPro?: boolean;
  isBasic?: boolean;
  userAttributes?: {
    email?: string;
    given_name?: string;
    family_name?: string;
    'custom:logo'?: string;
    'custom:feature_pln'?: string;
  };
};

export type AuthFlowState =
  | 'email'
  | 'confirm'
  | 'signup'
  | 'finish'
  | 'finish-signup';
export type AuthFlow = {
  state: AuthFlowState;
  validationFrom?: Exclude<
    AuthFlowState,
    'confirm' | 'finish' | 'finish-signup'
  > | null;
  persistentData: {
    email?: string;
  };
};

export type TAuthStore = {
  isInit: boolean;
  isLoggedIn: boolean;
  user: UserData | null;
  hasAnyAccess?: boolean;
};
export const $auth = map<TAuthStore>({
  isInit: true,
  isLoggedIn: false,
  user: null,
});

export const $hasAnyAccess = atom<boolean>(false);

$auth.subscribe((state) => {
  const hasAnyAccess =
    state.user?.isAdmin || state.user?.isPro || state.user?.isBasic;
  $hasAnyAccess.set(hasAnyAccess || false);
});

export const $authFlow = map<AuthFlow>({
  state: 'email',
  persistentData: {
    email: undefined,
  },
});

export function logoutUser(cb?: () => void) {
  $auth.setKey('isLoggedIn', false);
  $auth.setKey('user', null);
  $auth.setKey('isInit', true);
  $authFlow.setKey('state', 'email');
  $authFlow.setKey('persistentData', {
    email: undefined,
  });

  signOut()
    .catch((error) => {
      console.error('Error signing out:', error);
    })
    .finally(async () => {
      await clearUserCache();
      if (cb) {
        return cb?.();
      }
      window.location.reload();
    });
}

function getUserGroups(authSession: any) {
  const groups = (authSession?.tokens?.accessToken.payload['cognito:groups'] ||
    []) as string[];
  const licenseGroup = groups.find((group) =>
    group.startsWith('licensegroup_'),
  );
  return licenseGroup || undefined;
}

const { REACT_APP_REVENUECAT_PUBLIC_WEB_API_KEY } = process.env || {};

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);
async function initRevenueCat(userId: string): Promise<{
  isAdmin: boolean;
  isPro: boolean;
  isBasic: boolean;
  customerInfo: CustomerInfo;
} | null> {
  if (!REACT_APP_REVENUECAT_PUBLIC_WEB_API_KEY || !userId) {
    console.error('Error initializing RevenueCat: no API key or userId');
    return null;
  }
  try {
    const isAlreadyConfigured = Purchases.isConfigured();
    if (!isAlreadyConfigured) {
      if (isLocalhost) {
        await Purchases.setLogLevel(LogLevel.Debug);
      }

      await Purchases.configure({
        apiKey: REACT_APP_REVENUECAT_PUBLIC_WEB_API_KEY,
        appUserId: userId,
      });
    }
    const instance = await Purchases.getSharedInstance();
    const customerInfo = await instance.getCustomerInfo();
    return {
      isAdmin: await instance.isEntitledTo('Admin'),
      isPro: await instance.isEntitledTo('Pro'),
      isBasic: await instance.isEntitledTo('Basic'),
      customerInfo: customerInfo,
    };
  } catch (error) {
    console.error('Error initializing RevenueCat: no API key/userId', error);
    return null;
  }
}

export const getUser = async () => {
  // If stored implementation here
  try {
    return await fetchUserData();
  } catch (error) {
    throw error;
  }
};

type UserDataCache = {
  data: UserData;
  storeTime: number;
};

async function cacheUser(user: UserData) {
  // Store to cache
  try {
    const authCache = await caches.open('auth-cache');
    const blob = new Blob(
      [
        JSON.stringify({
          data: user,
          storeTime: Date.now(),
        }),
      ],
      { type: 'application/json' },
    );
    const response = new Response(blob);
    await authCache.put('user', response);
    console.log('User cached successfully');
  } catch (e) {
    console.error('Error caching user:', e);
  }
}

async function getCachedUser() {
  try {
    const authCache = await caches.open('auth-cache');
    const cachedResponse = await authCache.match('user');
    if (cachedResponse) {
      const user = await cachedResponse.json();
      console.log('User retrieved from cache', user);
      return user as UserDataCache;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
}

async function clearUserCache() {
  try {
    const authCache = await caches.open('auth-cache');
    await authCache.delete('user');
    console.log('User cache cleared');
  } catch (e) {
    console.error('Error clearing user cache:', e);
  }
}

function setUserAccessData(user: UserData) {
  $auth.setKey('isLoggedIn', true);
  $auth.setKey('user', user);
}
export async function fetchUserData() {
  let error: string | null = null;
  let response: UserData | null = null;
  try {
    console.log('fetchUserData');
    let user: GetCurrentUserOutput | null = null;
    try {
      user = await getCurrentUser();
    } catch (e) {
      console.log('Error fetching user data:', e);
    }
    console.log('user', user);
    if (user && $isOnline.get()) {
      const [userAttributes, authSession, accessData = {}] = await Promise.all([
        fetchUserAttributes(),
        fetchAuthSession(),
        initRevenueCat(user.userId),
      ]);
      console.log('accessData', accessData);
      const licenseGroup = getUserGroups(authSession);

      const updatedUser = {
        userAttributes,
        data: { ...user, licenseGroup },
        ...accessData, // Include access data from RevenueCat},
      };
      void cacheUser(updatedUser as UserData);

      response = updatedUser as UserData;
      setUserAccessData(response);
    } else {
      console.log('user is null, get cached user');
      const cachedUser = await getCachedUser();
      console.log('cachedUser', cachedUser);

      if (cachedUser && !$isOnline.get()) {
        console.log('cachedUser:howManyDaysOld', cachedUser);
        const howManyDaysOld =
          (Date.now() - cachedUser.storeTime) / (1000 * 60 * 60 * 24);
        if (howManyDaysOld < 15) {
          console.log('cachedUser:setUserAccessData', cachedUser);
          setUserAccessData(cachedUser.data);
          response = cachedUser.data;
        } else {
          void clearUserCache();
          error = 'UNAUTHORIZED';
        }
      } else {
        error = 'UNAUTHORIZED';
      }
    }
  } catch (e) {
    console.error('Error fetching user data:', e);
    error = 'INTERNAL_ERROR';
  }
  if (error) {
    throw new Error(error);
  }
  return response;
}
